import api from "./axios";
import { chatFileUrl } from "../utils/chatFiles";

/**
 * Send a message request
 * @param {string} to - Recipient user ID
 * @param {string} message - Optional message
 * @returns {Promise} API response
 */
export const sendMessageRequest = async (to, message = "") => {
  const response = await api.post("/messages/request", { to, message });
  return response.data;
};

/**
 * Get message requests
 * @param {string} type - 'sent' or 'received'
 * @returns {Promise} API response
 */
export const getMessageRequests = async (type = null) => {
  const params = type ? { type } : {};
  const response = await api.get("/messages/requests", { params });
  return response.data;
};

/**
 * Accept or reject a message request
 * @param {string} requestId - Request ID
 * @param {string} action - 'accept' or 'reject'
 * @returns {Promise} API response
 */
export const respondToMessageRequest = async (requestId, action) => {
  const response = await api.put(`/messages/request/${requestId}`, { action });
  return response.data;
};

/**
 * Get all conversations for the authenticated user
 * @returns {Promise} API response
 */
export const getConversations = async () => {
  const response = await api.get("/messages/conversations");
  return response.data;
};

/**
 * Get messages for a conversation
 * @param {string} conversationId - Conversation ID
 * @param {number} page - Page number
 * @param {number} limit - Messages per page
 * @returns {Promise} API response
 */
export const getMessages = async (conversationId, page = 1, limit = 50) => {
  const response = await api.get(`/messages/conversation/${conversationId}`, {
    params: { page, limit },
  });
  return response.data;
};

/**
 * Send a message
 * @param {string} conversationId - Conversation ID
 * @param {string} content - Message content
 * @returns {Promise} API response
 */
export const sendMessage = async (conversationId, content, replyTo = null) => {
  const response = await api.post("/messages", { conversationId, content, replyTo });
  return response.data;
};

/**
 * Send a photo, video or file as a message, with an optional caption
 * @param {string} conversationId - Conversation ID
 * @param {File} file - What to send
 * @param {{ content?: string, replyTo?: string|null, onProgress?: (fraction: number) => void }} [options]
 * @returns {Promise} API response; data is the message, as sendMessage's
 */
export const sendMessageAttachment = async (conversationId, file, { content = "", replyTo = null, onProgress } = {}) => {
  const form = new FormData();
  form.append("conversationId", conversationId);
  if (content) form.append("content", content);
  if (replyTo) form.append("replyTo", replyTo);
  // Multer reads names as latin1; this keeps Georgian and other names intact.
  form.append("fileName", file.name);
  form.append("file", file);
  const response = await api.post("/messages/attachment", form, {
    onUploadProgress: (e) => onProgress?.(e.total ? e.loaded / e.total : 0),
  });
  return response.data;
};

/** Where a message's photo, video or file is shown from — or saved, with `{ download: true }`. */
export const messageFileUrl = (messageId, options) => chatFileUrl(`/messages/${messageId}/file`, options);

/**
 * Mark messages as read
 * @param {string} conversationId - Conversation ID
 * @returns {Promise} API response
 */
export const markAsRead = async (conversationId) => {
  const response = await api.put(`/messages/read/${conversationId}`);
  return response.data;
};

/**
 * Check if users can message
 * @param {string} userId - Other user ID
 * @returns {Promise} API response
 */
export const checkCanMessage = async (userId) => {
  const response = await api.get(`/messages/check/${userId}`);
  return response.data;
};

/**
 * Block a user
 * @param {string} userId - User ID to block
 * @returns {Promise} API response
 */
export const blockUser = async (userId) => {
  const response = await api.post(`/messages/block/${userId}`);
  return response.data;
};

/**
 * Unblock a user
 * @param {string} userId - User ID to unblock
 * @returns {Promise} API response
 */
export const unblockUser = async (userId) => {
  const response = await api.post(`/messages/unblock/${userId}`);
  return response.data;
};

/**
 * Check if user is blocked
 * @param {string} userId - User ID to check
 * @returns {Promise} API response
 */
export const checkBlocked = async (userId) => {
  const response = await api.get(`/messages/blocked/${userId}`);
  return response.data;
};

/**
 * React to a message
 * @param {string} messageId - Message ID
 * @param {string} emoji - Emoji to react with (default: 👍)
 * @returns {Promise} API response
 */
export const reactToMessage = async (messageId, emoji = "👍") => {
  const response = await api.put(`/messages/react/${messageId}`, { emoji });
  return response.data;
};

/*
 * Group chats. Their messages, files and reactions go through the calls above.
 * @see mintly-back/src/controllers/groupChatController.js
 */

/** People to add to a group: by name, or the people you chat with when `q` is empty. */
export const searchGroupCandidates = async (q = "") => {
  const response = await api.get("/messages/groups/candidates", { params: q ? { q } : {} });
  return response.data;
};

/** Start a group; data is the conversation, as getConversations lists them. */
export const createGroup = async (name, memberIds) => {
  const response = await api.post("/messages/groups", { name, members: memberIds });
  return response.data;
};

export const renameGroup = async (groupId, name) => {
  const response = await api.patch(`/messages/groups/${groupId}`, { name });
  return response.data;
};

export const addGroupMembers = async (groupId, memberIds) => {
  const response = await api.post(`/messages/groups/${groupId}/members`, { members: memberIds });
  return response.data;
};

/** Leave a group; `myId` is your own id. */
export const leaveGroup = async (groupId, myId) => {
  const response = await api.delete(`/messages/groups/${groupId}/members/${myId}`);
  return response.data;
};

/** Remove someone from a group (its moderator and admins). */
export const removeGroupMember = async (groupId, userId) => {
  const response = await api.delete(`/messages/groups/${groupId}/members/${userId}`);
  return response.data;
};

/** Make someone in a group an admin (its moderator). */
export const makeGroupAdmin = async (groupId, userId) => {
  const response = await api.post(`/messages/groups/${groupId}/admins`, { userId });
  return response.data;
};

/** Make an admin an ordinary member again (its moderator). */
export const removeGroupAdmin = async (groupId, userId) => {
  const response = await api.delete(`/messages/groups/${groupId}/admins/${userId}`);
  return response.data;
};
